import React, { useState } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EditableTranslation - A component that allows admins to edit translations inline
 * 
 * Usage:
 * <EditableTranslation translationKey="help.browseNews" fallback="Browse News">
 *   {translatedText}
 * </EditableTranslation>
 */
const EditableTranslation = ({ 
  children, 
  translationKey, 
  className = '',
  as: Component = 'span',
  multiline = false,
  onSave 
}) => {
  const { isAdmin } = useAuth();
  const { language, updateTranslation } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(children || '');
  const [saving, setSaving] = useState(false);

  // Only show edit functionality for admins
  if (!isAdmin) {
    return <Component className={className}>{children}</Component>;
  }

  const handleEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditValue(children || '');
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (editValue === children) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${BACKEND_URL}/api/admin/translations`, {
        key: translationKey,
        language: language,
        value: editValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local translation cache
      if (updateTranslation) {
        updateTranslation(translationKey, editValue);
      }
      
      if (onSave) {
        onSave(editValue);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save translation:', error);
      alert('Failed to save translation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(false);
    setEditValue(children || '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave(e);
    } else if (e.key === 'Escape') {
      handleCancel(e);
    }
  };

  if (isEditing) {
    return (
      <div className="inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>
        {multiline ? (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="px-2 py-1 text-sm border border-emerald-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-w-[200px] resize-none"
            rows={3}
            autoFocus
            disabled={saving}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="px-2 py-0.5 text-sm border border-emerald-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 min-w-[100px]"
            autoFocus
            disabled={saving}
          />
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded text-emerald-600"
          title="Save"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={handleCancel}
          disabled={saving}
          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600"
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <Component 
      className={`${className} relative group/edit inline-flex items-center gap-1`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && (
        <button
          onClick={handleEdit}
          className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full opacity-80 hover:opacity-100 transition-opacity ml-1"
          title="Edit translation"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </Component>
  );
};

/**
 * EditableArticleTitle - Specifically for article headlines that can be edited by admin
 */
export const EditableArticleTitle = ({ 
  article, 
  translatedTitle, 
  className = '',
  onSave 
}) => {
  const { isAdmin } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(translatedTitle || article.title);
  const [saving, setSaving] = useState(false);

  const displayTitle = translatedTitle || article.title;

  if (!isAdmin) {
    return <span className={className}>{displayTitle}</span>;
  }

  const handleEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditValue(displayTitle);
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (editValue === displayTitle) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.put(`${BACKEND_URL}/api/articles/${article.id}`, {
        title: editValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (onSave) {
        onSave(editValue);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save article title:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(false);
    setEditValue(displayTitle);
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-1" onClick={e => e.stopPropagation()}>
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancel(e);
          }}
          className="flex-1 px-2 py-1 text-sm border border-emerald-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          rows={2}
          autoFocus
          disabled={saving}
        />
        <div className="flex flex-col gap-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="p-1 bg-emerald-500 hover:bg-emerald-600 rounded text-white"
            title="Save"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="p-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 rounded text-gray-700 dark:text-white"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <span 
      className={`${className} relative inline-flex items-start gap-1`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {displayTitle}
      {isHovered && (
        <button
          onClick={handleEdit}
          className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full opacity-80 hover:opacity-100 transition-opacity"
          title="Edit headline"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

export default EditableTranslation;
